import { AxiosInstance } from 'axios'
import { IJsonApiSerializer } from '../JsonApiSerializer'

class PostService {
  public httpClient: AxiosInstance
  public serializer: IJsonApiSerializer

  constructor(httpClient: AxiosInstance, serializer: IJsonApiSerializer) {
    this.httpClient = httpClient
    this.serializer = serializer
  }

  public getDataFromJson(JsonData) {
    const data = this.serializer.parseResourceData(JsonData, JsonData.data)
    const pagination = { ...JsonData.meta }

    return { data, pagination }
  }

  public getPostFromJson(data) {
    const post = this.serializer.parseResourceData(data, data.data)

    return { post }
  }

  public getPostsFromJson(data) {
    const posts = this.serializer.parseResourceData(data, data.data)
    const pagination = { ...data.meta }

    return { posts, pagination }
  }

  public async toggleLike(post) {
    const { data } = await this.httpClient.put(`/api/posts/${post.id}/toggle_like`)

    return { like: data.like }
  }

  public async create(params) {
    const { data } = await this.httpClient.post('/agent/api/posts', { post: params })

    return data
  }

  public async update(params) {
    const { data } = await this.httpClient.patch(`/agent/api/posts/${params.id}`, { post: params })
    const { flush } = data
    const post = this.serializer.parseResourceData(data.post, data.post.data)

    return { post, flush }
  }

  public async updateAllowPickup(id, allow_pickup) {
    const { data } = await this.httpClient.patch(`/agent/api/posts/${id}/allow_pickup`, {
      post: { allow_pickup },
    })

    return data
  }

  public async updatePostOperationSetting(postId, params) {
    const { data } = await this.httpClient.patch(
      `/agent/api/posts/${postId}/post_operation_setting`,
      {
        post: params,
      }
    )
    const { flush } = data
    const post = this.serializer.parseResourceData(data.post, data.post.data)

    return { post, flush }
  }

  public async updatePostTourSkill(postId, params) {
    const { data } = await this.httpClient.patch(`/agent/api/posts/${postId}/post_tour_skill`, {
      post: params,
    })
    const { flush } = data
    const post = this.serializer.parseResourceData(data.post, data.post.data)

    return { post, flush }
  }

  public async updatePostDetail(params) {
    const { data } = await this.httpClient.patch(`/agent/api/post_details/${params.id}`, {
      post_detail: params,
    })
    const { flush } = data
    const postDetail = this.serializer.parseResourceData(data.post_detail, data.post_detail.data)

    return { postDetail, flush }
  }
  public async updateBokunLink(params) {
    const { data } = await this.httpClient.put(`/agent/api/posts/${params.id}/update_bokun_link`, {
      post: params,
    })
    const { flush } = data
    const post = this.serializer.parseResourceData(data.post, data.post.data)

    return { post, flush }
  }

  public async updateSlug(params) {
    const { data } = await this.httpClient.put(`/agent/api/posts/${params.id}/update_slug`, {
      post: params,
    })
    const { flush } = data
    const post = this.serializer.parseResourceData(data.post, data.post.data)

    return { post, flush }
  }

  public async closePost(id) {
    const { data } = await this.httpClient.put(`/agent/api/posts/${id}/close`)

    return { flush: data.flush }
  }

  public async publish(params) {
    const { data } = await this.httpClient.put(`/agent/api/posts/${params.id}/publish`, {})
    const { post_path, flush } = data

    return { post_path, flush }
  }

  public async limitedPublish(params) {
    const { data } = await this.httpClient.put(`/agent/api/posts/${params.id}/limited_publish`, {})
    const { post_path, flush } = data

    return { post_path, flush }
  }

  public async suspend(params) {
    const { data } = await this.httpClient.put(`/agent/api/posts/${params.id}/suspend`, {})
    const { flush, post, post_path } = data

    return { flush, post, post_path }
  }

  public async search(params) {
    const { data } = await this.httpClient.get('/api/posts/search', { params })
    const posts = this.serializer.parseResourceData(data.posts.posts, data.posts.posts.data)
    const pagination = data.posts.posts.meta

    return { posts, pagination }
  }

  public async searchOwnPost(params) {
    const { data } = await this.httpClient.get('/agent/api/posts/search_from_own', { params })
    const posts = this.serializer.parseResourceData(data.posts.posts, data.posts.posts.data)
    const pagination = data.posts.posts.meta

    return { posts, pagination }
  }

  public async delete(id: number) {
    const { data } = await this.httpClient.delete(`/agent/api/posts/${id}`)
    const { flush } = data
    return { flush }
  }

  public async getFavorites(params) {
    const { data } = await this.httpClient.get('/api/favorites', { params })
    const posts = this.serializer.parseResourceData(data.posts.posts, data.posts.posts.data)
    const pagination = data.posts.posts.meta

    return { posts, pagination }
  }

  public async getCalculation(post_id, params) {
    const { data } = await this.httpClient.get(`/api/posts/${post_id}/calculate`, { params })

    return { price: data.price }
  }
  public async getChargePrice(params) {
    const { data } = await this.httpClient.post(`/api/check_coupon_validity`, { ...params })
    const { charge_price, flush } = data

    return { charge_price, flush }
  }
  public async getRelatedPosts(post) {
    const { data } = await this.httpClient.get(`/api/related_posts/${post.id}`)
    const relatedPosts = this.serializer.parseResourceData(data.posts, data.posts.data)

    return { relatedPosts }
  }

  public async getBokunActivities() {
    const { data } = await this.httpClient.post(`/agent/api/bokun_activities/sync`)
    return { data }
  }

  public async getStartTimes(id, day) {
    const { data } = await this.httpClient.get(`/agent/api/posts/${id}/start_times?day=${day}`)
    return data.start_times
  }

  public async createReview(formData) {
    return this.httpClient.post(`/api/reviews`, formData)
  }

  public async createReviewForDirectReservation(formData) {
    return this.httpClient.post(`/api/reviews/create_for_non_signed_in`, formData)
  }

  public async createReviewForTokenIssuedLink(formData) {
    return this.httpClient.post(`/api/reviews/create_for_token_issued_link`, formData)
  }

  public async updatePostGuideRankFees(post_id, params) {
    const { data } = await this.httpClient.patch(
      `/agent/api/posts/${post_id}/post_guide_rank_fees`,
      params
    )
    const { flush } = data
    const post_rank_fees = this.serializer.parseResourceData(
      data.post_rank_fees,
      data.post_rank_fees.data
    )
    return { post_rank_fees, flush }
  }

  public async getReviewImages(id) {
    const { data } = await this.httpClient.get(`/api/posts/${id}/review_images`)
    const reviewImages = this.serializer.parseResourceData(
      data.review_images,
      data.review_images.data
    )
    return { reviewImages }
  }

  public async createPostCost(params) {
    const { data } = await this.httpClient.post(`/agent/api/post_costs`, params)
    const { flush } = data
    return { flush }
  }

  public async updatePostCost(id, params) {
    const { data } = await this.httpClient.patch(`/agent/api/post_costs/${id}`, params)
    const { flush } = data
    return { flush }
  }
}

export default PostService
